import { Dayjs } from "dayjs";
import { useField, useFormikContext } from "formik";

import { Box, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker, TimePickerProps } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

interface CustomTimePickerProps extends TimePickerProps<Dayjs> {
  name: string;
  label?: string;
  setOverlapError?: (e: string) => void;
}

const CustomTimePicker = ({
  name,
  label,
  setOverlapError,
  ...rest
}: CustomTimePickerProps) => {
  const [field, meta] = useField(name);
  const { setFieldTouched, setFieldValue } = useFormikContext();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {!!label && (
          <Typography
            sx={{
              color: "var(--neutral-1000)",
              font: { xs: "var(--body-xs)", md: "var(--body-sm)" },
              mb: "8px",
            }}
          >
            {label}
          </Typography>
        )}
        <TimePicker
          desktopModeMediaQuery="@media(min-width: 0px)" // forces desktop UI
          value={field.value}
          onChange={(newValue) => {
            setFieldValue(name, newValue);
            setFieldTouched(name, true, false);
            setOverlapError?.("");
          }}
          onClose={() => setFieldTouched(name, true, true)}
          // disablePast
          closeOnSelect={false}
          slotProps={{
            textField: {
              required: true,
              error: meta.touched && !!meta.error,
              helperText: meta.touched && meta.error,
              fullWidth: true,
              placeholder: "hh:mm (am/pm)",
              onBlur: () => setFieldTouched(name, true, true),
            },
            popper: {
              disablePortal: true, // Keeps popper inside modal DOM
              onClick: (e: any) => e.stopPropagation(), // Prevent modal close
              placement: "top-end",
            },
          }}
          sx={{
            fontFamily: '"Lexend", sans-serif',
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "var(--neutral-50)",
              height: "44px",
              padding: "12px 14px",
              font: "var(--body-sm)",
              "& fieldset": {
                borderColor: "var(--neutral-200)",
              },
              "& placeholder": {
                color: "var(--neutral-400)",
              },
              "& .MuiInputBase-input": {
                padding: "0",
              },
            },
          }}
          {...rest}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CustomTimePicker;
